import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as authActions from "../Authentication/redux/actions";

/**
 * Update KYC Information (2.8.0) — reading and editing `customer_unitholders.kyc_expired_at`.
 *
 * That column is the "Next KYC review date" be-customer gates the in-app Update KYC screen on.
 * MF-RGT will own it once it ships the field on CustomerProfiles; until then the only thing that
 * writes it is the customer confirming in the app, so an operator has no way to set up a test case
 * — or to correct a wrong date — without this screen.
 */

/** The customer detail path. Fronted by the API gateway for as long as this page has existed. */
const CUSTOMER_PATH = "/api/v1/admin-actions/customers";

/**
 * Whether to call the KYC review endpoints on their own routes.
 *
 * be-admin registers a dedicated GET/PATCH pair on `/customers/:uuid/kyc-review`, but each is a NEW
 * path, and a new path answers nothing until someone creates the matching API gateway resource —
 * the caller just gets the gateway's own 401/403/404. Until those exist, the same two service
 * methods are reached through paths that are already fronted: `?fields=kycReview` on the customer
 * GET, and `action: "kyc-expiry"` in the body of the account-status PATCH.
 *
 * Default is the carrier form, because that is the one that works today. Set
 * REACT_APP_KYC_REVIEW_DEDICATED_ROUTES=true once the gateway resources are in place; both forms
 * hit the same be-admin service methods, so nothing else has to change. Same arrangement as
 * REACT_APP_NAV_DEDICATED_ROUTES in NavList/api.js.
 */
const USE_DEDICATED_KYC_ROUTES =
  process.env.REACT_APP_KYC_REVIEW_DEDICATED_ROUTES === "true";

export function kycReviewReadEndpoint(customerUuid) {
  return USE_DEDICATED_KYC_ROUTES
    ? { path: `${CUSTOMER_PATH}/${customerUuid}/kyc-review`, params: {} }
    : { path: `${CUSTOMER_PATH}/${customerUuid}`, params: { fields: "kycReview" } };
}

export function kycReviewWriteEndpoint(customerUuid) {
  return USE_DEDICATED_KYC_ROUTES
    ? { path: `${CUSTOMER_PATH}/${customerUuid}/kyc-review`, carryAction: false }
    : { path: `${CUSTOMER_PATH}/${customerUuid}/status`, carryAction: true };
}

export class KycReviewRequestError extends Error {
  constructor(status, detail) {
    super(detail || "KYC review request failed");
    this.name = "KycReviewRequestError";
    this.status = status;
    this.detail = detail;
  }
}

function buildQueryString(params) {
  const parts = [];
  Object.keys(params || {}).forEach(key => {
    const value = params[key];
    if (value === undefined || value === null || value === "") {
      return;
    }
    parts.push(encodeURIComponent(key) + "=" + encodeURIComponent(value));
  });
  return parts.length > 0 ? "?" + parts.join("&") : "";
}

/**
 * Request helper for the two KYC review calls.
 *
 * There is no axios interceptor in this dashboard: every caller attaches the admin headers by hand
 * and handles 401 itself. A 401 signs the operator out and resolves to `{ signedOut: true }` so the
 * caller stops quietly instead of rendering an error nobody will read.
 */
export function useKycReviewRequest() {
  const accessToken = useSelector(state => state.auth.accessToken);
  const tGUID = useSelector(state => state.auth.GUID);
  const dispatch = useDispatch();

  return useCallback(
    async (endpoint, { method = "GET", body = null } = {}) => {
      const url =
        process.env.REACT_APP_API_ADMIN_URL +
        endpoint.path +
        buildQueryString(endpoint.params);

      const request = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          userid: tGUID,
          "x-id-token": "Bearer " + accessToken
        },
        body: body ? JSON.stringify(body) : undefined
      });

      if (request.status === 401) {
        dispatch(authActions.autoSignOut());
        return { signedOut: true };
      }

      let response = null;
      try {
        response = await request.json();
      } catch (error) {
        response = null;
      }

      if (!request.ok) {
        const detail =
          response &&
          (response.message || (response.errors && response.errors[0]));
        throw new KycReviewRequestError(request.status, detail);
      }

      return { signedOut: false, data: (response && response.data) || [] };
    },
    [accessToken, tGUID, dispatch]
  );
}

/**
 * Builds the PATCH body. `action` is what the carrier dispatches on and is harmless on the
 * dedicated route, so it is always sent — one body shape for both forms.
 */
export function buildKycExpiryBody(unitholderId, kycExpiredAt) {
  return {
    action: "kyc-expiry",
    unitholderId: unitholderId || undefined,
    // null clears the date, which is a real instruction: it puts the row back to "we do not know",
    // which be-customer reads as NOT due. An empty input means exactly that.
    kycExpiredAt: kycExpiredAt ? kycExpiredAt : null
  };
}
